import * as THREE from 'three'
import { DemoScene } from './demo/scene'
import { writeDepthType, writeFps } from './demo/stats'
import { describeDepthGrant, hasHitTest, SESSION_INIT } from './depth/session'
import { createGate } from './ui/gate'
import { createHud } from './ui/hud'

/**
 * Entry point: the gate, the XR session, and the frame loop.
 *
 * Everything that touches depth lives under `src/demo` and `src/depth`; this
 * file exists to start a session and hand each frame to `DemoScene`.
 */

const FPS_WINDOW_SECONDS = 0.5

const root = document.getElementById('demo-root')
if (!root) throw new Error('#demo-root is missing from index.html')

// `dom-overlay` wants a top-level element; nesting it under the canvas host
// makes Chrome drop pointer events on the buttons.
const overlayHost = document.createElement('div')
overlayHost.className = 'demo-overlay-host'
document.body.append(overlayHost)

const gate = createGate(start)
overlayHost.append(gate.element)

async function start(): Promise<void> {
  const xr = navigator.xr
  if (!xr) throw new Error('No navigator.xr — this needs Chrome on Android with ARCore.')

  const session = await xr.requestSession('immersive-ar', {
    ...SESSION_INIT,
    domOverlay: { root: overlayHost },
  })

  const grant = describeDepthGrant(session)
  if (!grant.granted) {
    await session.end()
    throw new Error(grant.reason ?? 'Depth sensing is unavailable on this device.')
  }

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.xr.enabled = true
  // `local-floor` is optional, may not be granted, and cannot be changed
  // mid-session. Both spaces are gravity-aligned; `local-floor` only moves the
  // origin, which nothing here depends on.
  renderer.xr.setReferenceSpaceType('local')
  root!.prepend(renderer.domElement)
  await renderer.xr.setSession(session)

  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(
    70,
    window.innerWidth / window.innerHeight,
    0.01,
    20
  )
  scene.add(new THREE.HemisphereLight(0xffffff, 0x334455, 2))

  gate.remove()

  let frozen = false
  const hud = createHud(overlayHost, {
    onToggleFreeze: (next) => {
      frozen = next
    },
    onReset: () => demo.reset(),
  })

  const demo = new DemoScene({ scene, camera, renderer, session, hud })

  writeDepthType(hud, grant.type)
  if (!hasHitTest(session)) {
    hud.setNote('hit-test was not granted — the analytic floor is unavailable.', 'warn')
  }

  let elapsed = 0
  let frames = 0
  let lastTime = 0

  renderer.setAnimationLoop((time, frame) => {
    const delta = lastTime === 0 ? 0 : (time - lastTime) / 1000
    lastTime = time

    if (delta > 0) {
      elapsed += delta
      frames += 1
      if (elapsed >= FPS_WINDOW_SECONDS) {
        writeFps(hud, frames / elapsed)
        elapsed = 0
        frames = 0
      }
    }

    const refSpace = renderer.xr.getReferenceSpace()
    if (refSpace && !frozen) demo.update(frame, refSpace)

    renderer.render(scene, camera)
  })

  session.addEventListener('end', () => {
    renderer.setAnimationLoop(null)
    demo.dispose()
    hud.dispose()
    renderer.dispose()
    renderer.domElement.remove()
    window.location.reload()
  })
}
